import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { node, func, oneOfType, array, string, number, bool } from 'prop-types';
import { isNil } from 'lodash';
import useMenuContext from '../useMenuContext';

export const MenuGroupContext = React.createContext({});

const MenuGroup = ({
    checked,
    onChange = Function.prototype,
    name,
    isMulti = false,
    shouldCloseOnClick = false,
    children
}) => {
    if (!children) {
        throw new Error('<Menu.Group/> must get a children prop');
    }

    const { selectable } =  useMenuContext();

    const getInitialCheckedState = () => {
        if (selectable && isMulti) {
            if (isNil(checked)) {
                return [];
            }

            if (!Array.isArray(checked)){
                throw new Error('Must pass an array checked prop when isMulti is in usage');
            }

        }
        return checked;
    };

    const [currentChecked, setCurrentChecked] = useState(getInitialCheckedState());

    useEffect(() => {
        setCurrentChecked(checked);

    }, [checked]);

    const setChecked = useCallback(
        (itemValue) => {
            if (!selectable) {
                return;
            }

            if (!isMulti) {
                onChange(itemValue);
                setCurrentChecked(itemValue);

                return itemValue;
            }

            const newChecked = currentChecked.includes(itemValue) ?
                currentChecked.filter((value) => value !== itemValue) :
                [...currentChecked, itemValue];

            onChange(newChecked);
            setCurrentChecked(newChecked);

        },
        [currentChecked, onChange, selectable, isMulti]
    );

    const context = useMemo(() =>
        ({ checked: currentChecked, setChecked, selectable, isMulti, shouldCloseOnClick }),
    [currentChecked, setChecked, selectable, isMulti, shouldCloseOnClick]);

    return (
        <MenuGroupContext.Provider value={context}>
            {React.Children.map(children, (child) =>
                React.cloneElement(child, {
                    ...child.props,
                    name
                })
            )}
        </MenuGroupContext.Provider>
    );
};

MenuGroup.propTypes = {
    /**
     * The group nodes
     */
    children: node.isRequired,
    /**
     * onChange (value\values checked) event handler
     */
    onChange: func,
    /**
     * Checked items state, can be none, single or multi valued
     */
    checked: oneOfType([array, string, number]),
    /**
     * A name in case the group is being used as a form input
     */
    name: string,
    /**
     * Whether or not the group is multi or single select
     */
    isMulti: bool,
    /**
     * Whether or not we should close wrapping element on item select
     */
    shouldCloseOnClick: bool
};

export default MenuGroup;
